import type { TypedWrappedFieldProps } from "redux-form";
import { touch as touchAction } from "redux-form";

import type {
  NumberInputProps,
  NumberInputWithButtonsProps,
  TextFieldProps as TextFieldPropsBUC,
} from "@braintrust/braintrust-ui-components";
import {
  DollarInput,
  NumberInput,
  NumberInputWithButtons,
} from "@braintrust/braintrust-ui-components";

import type { TextFieldErrorProps } from "../text/helper-text";
import { TextFieldHelperText } from "../text/helper-text";

type BaseTextFieldProps = DistributiveOmit<TextFieldPropsBUC, "onChange"> & {
  onChange?: NumberInputProps["onChange"];
};

export const ReduxFormTextFieldFactory =
  <T extends BaseTextFieldProps>(Component: React.ElementType) =>
  ({
    input,
    meta,
    helperText,
    mapErrorCode,
    hideError,
    showErrorWhen = "touched",
    ...props
  }: T & TypedWrappedFieldProps<string> & TextFieldErrorProps) => {
    const { error, submitFailed } = meta;
    const handleChange: NumberInputProps["onChange"] = (value) => {
      if (props.onChange) {
        props?.onChange(value);
      }
      input.onChange(value);
    };

    const isError =
      !!error && (meta[showErrorWhen] || submitFailed) && !hideError;
    const showHelperText = isError || !!helperText;

    return (
      <Component
        {...props}
        {...input}
        onChange={handleChange}
        onDrop={
          props.disabled
            ? () => {
                return null;
              }
            : input.onDrop
        }
        helperText={
          showHelperText ? (
            <TextFieldHelperText
              meta={meta}
              mapErrorCode={mapErrorCode}
              hideError={hideError}
              showErrorWhen={showErrorWhen}
              helperText={helperText}
            />
          ) : undefined
        }
        error={(submitFailed || meta[showErrorWhen]) && !!error}
      />
    );
  };

export type NumberFieldProps = TypedWrappedFieldProps<string> &
  NumberInputProps;

export const NumberField =
  ReduxFormTextFieldFactory<NumberFieldProps>(NumberInput);

export const NumberWithButtonsField = ({
  input,
  meta,
  helperText,
  mapErrorCode,
  hideError,
  showErrorWhen = "touched",
  ...props
}: TypedWrappedFieldProps<number> &
  TextFieldErrorProps &
  NumberInputWithButtonsProps) => {
  const { error, submitFailed } = meta;
  const touch = () => meta.dispatch(touchAction(meta.form, input.name));
  const handleChange: NumberInputWithButtonsProps["onChange"] = (value) => {
    if (props.onChange) {
      props?.onChange(value);
    }
    input.onChange(value);
    touch();
  };

  return (
    <NumberInputWithButtons
      {...props}
      {...input}
      onChange={handleChange}
      onDrop={
        props.disabled
          ? () => {
              return null;
            }
          : input.onDrop
      }
      helperText={
        <TextFieldHelperText
          meta={meta}
          mapErrorCode={mapErrorCode}
          hideError={hideError}
          showErrorWhen={showErrorWhen}
          helperText={helperText}
        />
      }
      error={(submitFailed || meta[showErrorWhen]) && !!error}
    />
  );
};

export type DollarNumberFieldProps = NumberInputProps &
  TypedWrappedFieldProps<string>;

export const DollarNumberField =
  ReduxFormTextFieldFactory<DollarNumberFieldProps>(DollarInput);
